import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { usePlaceSearch } from "@/hooks/usePlaceSearch";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * A form address field backed by Google Places — the same search the map uses,
 * in an ordinary input. Picking a suggestion (or pressing Enter on free text)
 * geocodes it and hands the caller `{ address, lat, lng, viewport }`, which is
 * why the facility form no longer asks anyone to type coordinates by hand.
 *
 * The dropdown depends on the optional `places` library; resolving does not
 * (see usePlaceSearch). So worst case this degrades to "type an address, press
 * Enter" rather than breaking.
 */
export function AddressAutocomplete({
  value, onChange, onResolved, placeholder, disabled, testId, autoFocus,
}) {
  const { maps, error } = useGoogleMaps();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef(null);

  const { suggestions, resolve, resolving, suppressNext, clearSuggestions } =
    usePlaceSearch({ maps, query: value });

  useEffect(() => {
    setHighlight(-1);
    if (suggestions.length) setOpen(true);
  }, [suggestions]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const run = useCallback((request, fallbackAddress) => {
    setNotice(null);
    resolve(request)
      .then((hit) => {
        // Prefer Google's normalized address over the raw typing, but keep
        // what the user picked if the geocoder hands back something emptier.
        onChange?.(hit.address || fallbackAddress);
        onResolved?.(hit);
      })
      .catch((err) => setNotice(err.message));
  }, [resolve, onChange, onResolved]);

  const pick = useCallback((suggestion) => {
    suppressNext();
    onChange?.(suggestion.description);
    clearSuggestions();
    setOpen(false);
    run({ placeId: suggestion.placeId }, suggestion.description);
  }, [run, onChange, suppressNext, clearSuggestions]);

  const submit = useCallback(() => {
    const q = (value || "").trim();
    if (!q) return;
    setOpen(false);
    run({ address: q }, q);
  }, [value, run]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setHighlight((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      // Always swallowed: this field lives inside a <form>, and a bare Enter
      // would otherwise submit it instead of resolving the address.
      e.preventDefault();
      if (open && highlight >= 0 && suggestions[highlight]) pick(suggestions[highlight]);
      else submit();
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value || ""}
          onChange={(e) => { onChange?.(e.target.value); setNotice(null); }}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          className="pl-8 pr-8"
          data-testid={testId}
        />
        {resolving && (
          <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid={testId ? `${testId}-results` : undefined}
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-border/70 bg-popover shadow-xl"
        >
          {suggestions.map((s, i) => (
            <li key={s.placeId}>
              <button
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "flex w-full items-start gap-2 px-3 py-2 text-left transition",
                  i === highlight ? "bg-secondary/60" : "hover:bg-secondary/40"
                )}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate text-sm">{s.primary}</span>
                  {s.secondary && (
                    <span className="block truncate text-xs text-muted-foreground">{s.secondary}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {(notice || error) && (
        <p className="mt-1.5 text-xs text-amber-400">
          {error ? "Address search is unavailable — the Google Maps key is missing or invalid." : notice}
        </p>
      )}
    </div>
  );
}
